// Procedural fallbacks for every texture the world system draws. Shared art keys
// (src/art/keys.ts) always win: these are only generated, under separate
// `world_fb_*` keys, when the art builders or the AI pass did not provide a key,
// so a missing texture never crashes a room.

import type Phaser from 'phaser';
import { TEX, TILE_FRAMES, TILESET_COLUMNS, TILESET_FRAME_COUNT } from '../../art/keys';
import { GAME_HEIGHT, type ParallaxLayer } from '../../config/constants';
import { COLORS, css, mix, shade } from '../../config/palette';
import type { SectorDef } from '../../core/sector';
import { Rng, hashSeed } from '../../core/rng';

type G = CanvasRenderingContext2D;

/** Creates a canvas texture once (no-op if the key exists). */
function canvasTex(
  scene: Phaser.Scene,
  key: string,
  w: number,
  h: number,
  draw: (g: G, w: number, h: number) => void,
): Phaser.Textures.CanvasTexture | null {
  if (scene.textures.exists(key)) return null;
  const tex = scene.textures.createCanvas(key, w, h);
  if (!tex) return null;
  draw(tex.context, w, h);
  tex.refresh();
  return tex;
}

/** Adds numeric frames laid out left → right. */
function stripFrames(scene: Phaser.Scene, key: string, fw: number, fh: number, count: number): void {
  const tex = scene.textures.get(key);
  for (let i = 0; i < count; i++) if (!tex.has(String(i))) tex.add(i, 0, i * fw, 0, fw, fh);
}

/** Biome of the room being built: props prefer that biome's painted textures. */
let stageBiome: string | null = null;
export function setStageBiome(biome: string | null): void {
  stageBiome = biome;
}
/** The current biome's painted prop texture, if loaded. */
function stageProp(scene: Phaser.Scene, piece: 'crate' | 'terminal'): string | null {
  const key = stageBiome ? `stage_${stageBiome}_${piece}` : null;
  return key && scene.textures.exists(key) ? key : null;
}

/** `key` if it exists, otherwise the generated fallback key. */
export function texOr(scene: Phaser.Scene, key: string, fallback: () => string): string {
  return scene.textures.exists(key) ? key : fallback();
}

/** True if the texture has a frame with that name (numeric frames are stored as strings). */
export function hasFrame(scene: Phaser.Scene, key: string, frame: number | string): boolean {
  return scene.textures.exists(key) && scene.textures.get(key).has(String(frame));
}

// ───────────────────────────── Generic FX ─────────────────────────────

export function softTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.soft, () => {
    const key = 'world_fb_soft';
    canvasTex(scene, key, 64, 64, (g) => {
      const grd = g.createRadialGradient(32, 32, 0, 32, 32, 32);
      grd.addColorStop(0, 'rgba(255,255,255,1)');
      grd.addColorStop(0.35, 'rgba(255,255,255,0.45)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, 64, 64);
    });
    return key;
  });
}

export function pixelTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.pixel, () => {
    const key = 'world_fb_pixel';
    canvasTex(scene, key, 4, 4, (g) => {
      g.fillStyle = '#ffffff';
      g.fillRect(0, 0, 4, 4);
    });
    return key;
  });
}

export function rainTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.rain, () => {
    const key = 'world_fb_rain';
    canvasTex(scene, key, 4, 40, (g) => {
      const grd = g.createLinearGradient(0, 0, 0, 40);
      grd.addColorStop(0, 'rgba(255,255,255,0)');
      grd.addColorStop(1, 'rgba(255,255,255,0.9)');
      g.fillStyle = grd;
      g.fillRect(1, 0, 2, 40);
    });
    return key;
  });
}

export function emberTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.ember, () => {
    const key = 'world_fb_ember';
    canvasTex(scene, key, 8, 8, (g) => {
      const grd = g.createRadialGradient(4, 4, 0, 4, 4, 4);
      grd.addColorStop(0, 'rgba(255,255,255,1)');
      grd.addColorStop(1, 'rgba(255,255,255,0)');
      g.fillStyle = grd;
      g.fillRect(0, 0, 8, 8);
    });
    return key;
  });
}

export function shardTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.shard, () => {
    const key = 'world_fb_shard';
    canvasTex(scene, key, 12, 8, (g) => {
      g.fillStyle = '#ffffff';
      g.beginPath();
      g.moveTo(0, 2);
      g.lineTo(9, 0);
      g.lineTo(12, 6);
      g.lineTo(3, 8);
      g.closePath();
      g.fill();
    });
    return key;
  });
}

// ───────────────────────────── Tileset ─────────────────────────────

/**
 * Tileset texture for the sector: the shared biome sheet if present, otherwise a
 * generated sheet with the same 28-frame layout (see TILE_FRAMES).
 */
export function tilesetTex(scene: Phaser.Scene, sector: SectorDef): string {
  return texOr(scene, sector.tileset, () => {
    const key = `world_fb_tiles_${sector.biome}`;
    const T = 48;
    const rows = Math.ceil(TILESET_FRAME_COUNT / TILESET_COLUMNS);
    const p = sector.palette;
    const base = mix(COLORS.steel1, p.fog, 0.25);
    canvasTex(scene, key, TILESET_COLUMNS * T, rows * T, (g) => {
      for (let f = 0; f < TILESET_FRAME_COUNT; f++) {
        const ox = (f % TILESET_COLUMNS) * T;
        const oy = Math.floor(f / TILESET_COLUMNS) * T;
        g.save();
        g.translate(ox, oy);
        drawFallbackTile(g, f, base, p.accent, p.accent2);
        g.restore();
      }
    });
    const tex = scene.textures.get(key);
    for (let f = 0; f < TILESET_FRAME_COUNT; f++) {
      if (!tex.has(String(f))) tex.add(f, 0, (f % TILESET_COLUMNS) * T, Math.floor(f / TILESET_COLUMNS) * T, T, T);
    }
    return key;
  });
}

function drawFallbackTile(g: G, f: number, base: number, accent: number, accent2: number): void {
  const T = 48;
  const plate = (c: number) => {
    g.fillStyle = css(c);
    g.fillRect(0, 0, T, T);
    g.fillStyle = css(shade(c, 0.7));
    g.fillRect(0, 23, T, 2);
    g.fillRect(23, 0, 2, T);
  };
  if (f < 16 || f === TILE_FRAMES.solidDetailA || f === TILE_FRAMES.solidDetailB) {
    const mask = f < 16 ? f : 15;
    plate(base);
    if (f === TILE_FRAMES.solidDetailA) {
      g.fillStyle = css(shade(base, 0.6));
      g.fillRect(10, 10, 28, 28);
      g.fillStyle = css(accent, 0.35);
      g.fillRect(14, 22, 20, 3);
    } else if (f === TILE_FRAMES.solidDetailB) {
      g.fillStyle = css(shade(base, 1.3));
      for (let i = 0; i < 4; i++) g.fillRect(8, 9 + i * 9, 32, 3);
    }
    g.fillStyle = css(COLORS.ink);
    if (!(mask & 2)) g.fillRect(T - 3, 0, 3, T);
    if (!(mask & 8)) g.fillRect(0, 0, 3, T);
    if (!(mask & 4)) g.fillRect(0, T - 3, T, 3);
    if (!(mask & 1)) {
      g.fillStyle = css(COLORS.steel4);
      g.fillRect(0, 0, T, 5);
      g.fillStyle = css(accent, 0.7);
      g.fillRect(0, 5, T, 2);
    }
    return;
  }
  switch (f) {
    case TILE_FRAMES.platformLeft:
    case TILE_FRAMES.platformMid:
    case TILE_FRAMES.platformRight:
    case TILE_FRAMES.platformSingle: {
      g.fillStyle = css(COLORS.steel3);
      g.fillRect(0, 0, T, 6);
      g.fillStyle = css(COLORS.steel2);
      g.fillRect(0, 6, T, 8);
      g.fillStyle = css(accent, 0.6);
      g.fillRect(0, 14, T, 2);
      g.strokeStyle = css(COLORS.steel2);
      g.lineWidth = 3;
      g.beginPath();
      for (let x = 0; x < T; x += 16) {
        g.moveTo(x, 16);
        g.lineTo(x + 8, 26);
        g.lineTo(x + 16, 16);
      }
      g.stroke();
      return;
    }
    case TILE_FRAMES.hazard: {
      plate(shade(base, 0.8));
      g.fillStyle = css(accent2);
      g.fillRect(0, 0, T, 8);
      g.fillStyle = css(COLORS.telegraph);
      for (let x = -T; x < T; x += 12) {
        g.beginPath();
        g.moveTo(x, 20);
        g.lineTo(x + 6, 20);
        g.lineTo(x + 18, 8);
        g.lineTo(x + 12, 8);
        g.closePath();
        g.fill();
      }
      return;
    }
    case TILE_FRAMES.backWall:
      plate(shade(base, 0.55));
      return;
    case TILE_FRAMES.decorVent:
      plate(shade(base, 0.55));
      g.fillStyle = css(COLORS.ink);
      for (let i = 0; i < 5; i++) g.fillRect(8, 8 + i * 7, 32, 3);
      return;
    case TILE_FRAMES.decorPipe:
      g.fillStyle = css(COLORS.steel2);
      g.fillRect(0, 16, T, 16);
      g.fillStyle = css(COLORS.steel3);
      g.fillRect(0, 18, T, 4);
      g.fillRect(20, 12, 8, 24);
      return;
    case TILE_FRAMES.decorGrate:
      plate(shade(base, 0.5));
      g.strokeStyle = css(COLORS.steel2);
      g.lineWidth = 1;
      for (let i = 4; i < T; i += 6) {
        g.beginPath();
        g.moveTo(i, 2);
        g.lineTo(i, T - 2);
        g.stroke();
      }
      return;
    case TILE_FRAMES.decorStripes:
      plate(shade(base, 0.55));
      g.fillStyle = css(COLORS.telegraph, 0.55);
      for (let x = -T; x < T; x += 16) {
        g.beginPath();
        g.moveTo(x, 34);
        g.lineTo(x + 8, 34);
        g.lineTo(x + 20, 22);
        g.lineTo(x + 12, 22);
        g.closePath();
        g.fill();
      }
      return;
  }
}

// ───────────────────────────── Parallax ─────────────────────────────

/** Parallax texture for one layer: the sector key if loaded/built, otherwise a generated layer. */
export function parallaxTex(scene: Phaser.Scene, sector: SectorDef, layer: ParallaxLayer): string {
  return texOr(scene, sector.parallax[layer], () => {
    const key = `world_fb_bg_${sector.biome}_${layer}`;
    const W = 1024;
    const H = GAME_HEIGHT;
    const rng = new Rng(hashSeed('world-parallax', sector.biome, layer));
    const p = sector.palette;
    canvasTex(scene, key, W, H, (g) => {
      if (layer === 'sky') drawSky(g, W, H, p, rng, sector.biome);
      else if (layer === 'fore') drawFore(g, W, H, rng);
      else drawSkyline(g, W, H, p, rng, layer);
    });
    return key;
  });
}

function drawSky(g: G, W: number, H: number, p: SectorDef['palette'], rng: Rng, biome: string): void {
  const grd = g.createLinearGradient(0, 0, 0, H);
  grd.addColorStop(0, css(p.skyTop));
  grd.addColorStop(0.65, css(p.skyBottom));
  grd.addColorStop(1, css(mix(p.skyBottom, p.fog, 0.5)));
  g.fillStyle = grd;
  g.fillRect(0, 0, W, H);
  // Faint stars / particles, kept away from the seam.
  for (let i = 0; i < 90; i++) {
    g.fillStyle = css(p.light, rng.float(0.08, 0.35));
    g.fillRect(rng.int(4, W - 6), rng.int(0, H * 0.55), rng.chance(0.15) ? 2 : 1, rng.chance(0.15) ? 2 : 1);
  }
  // A large soft light source (moon / furnace glow / singularity), centred so it never touches the seam.
  const cx = W * 0.62;
  const cy = H * 0.22;
  const glow = g.createRadialGradient(cx, cy, 0, cx, cy, 260);
  const tint = biome === 'graveyard' ? p.light : p.accent;
  glow.addColorStop(0, css(tint, 0.35));
  glow.addColorStop(0.25, css(tint, 0.12));
  glow.addColorStop(1, css(tint, 0));
  g.fillStyle = glow;
  g.fillRect(0, 0, W, H);
  g.fillStyle = css(mix(tint, 0xffffff, 0.3), biome === 'vault' ? 0.25 : 0.55);
  g.beginPath();
  g.arc(cx, cy, biome === 'vault' ? 40 : 56, 0, Math.PI * 2);
  g.fill();
}

/** Seamless skyline silhouettes: each block is drawn twice (x and x - W) so the edges join. */
function drawSkyline(g: G, W: number, H: number, p: SectorDef['palette'], rng: Rng, layer: ParallaxLayer): void {
  const cfg = {
    far: { base: H * 0.5, maxH: H * 0.38, minW: 30, maxW: 90, color: mix(p.skyBottom, p.fog, 0.45), lights: 0.35 },
    mid: { base: H * 0.64, maxH: H * 0.42, minW: 50, maxW: 140, color: mix(p.skyBottom, COLORS.ink, 0.35), lights: 0.2 },
    near: { base: H * 0.78, maxH: H * 0.5, minW: 24, maxW: 70, color: mix(p.skyTop, COLORS.ink, 0.5), lights: 0.08 },
  }[layer as 'far' | 'mid' | 'near'];
  const col = cfg.color;
  const block = (x: number, y: number, w: number, h: number) => {
    for (const ox of [0, -W, W]) g.fillRect(x + ox, y, w, h);
  };
  g.fillStyle = css(col);
  block(0, cfg.base, W, H - cfg.base);
  let x = 0;
  while (x < W) {
    const w = rng.int(cfg.minW, cfg.maxW);
    const h = rng.float(0.25, 1) * cfg.maxH;
    g.fillStyle = css(col);
    block(x, cfg.base - h, w, h + 2);
    if (rng.chance(0.4)) block(x + w * 0.4, cfg.base - h - 40, 3, 40); // antenna
    // Window lights / accent strips.
    for (let i = 0; i < (w * h) / 900; i++) {
      if (!rng.chance(cfg.lights)) continue;
      g.fillStyle = css(rng.chance(0.7) ? p.light : p.accent, rng.float(0.15, 0.45));
      block(x + rng.int(4, Math.max(5, w - 8)), cfg.base - h + rng.int(6, Math.max(7, h - 6)), 3, 2);
    }
    x += w + rng.int(0, layer === 'near' ? 120 : 30);
  }
  if (layer === 'near') {
    // Pipes / gantry beams crossing the near layer.
    g.fillStyle = css(col);
    block(0, cfg.base - 24, W, 10);
    for (let i = 0; i < 6; i++) block(rng.int(0, W), cfg.base - 220, 8, 200);
  }
}

function drawFore(g: G, W: number, H: number, rng: Rng): void {
  const ink = css(COLORS.ink, 0.95);
  g.fillStyle = ink;
  g.strokeStyle = ink;
  // Hanging cables along the top.
  g.lineWidth = 4;
  for (let i = 0; i < 4; i++) {
    const x0 = rng.int(0, W);
    const span = rng.int(200, 420);
    const sag = rng.int(40, 120);
    for (const ox of [0, -W, W]) {
      g.beginPath();
      g.moveTo(x0 + ox, -4);
      g.quadraticCurveTo(x0 + ox + span / 2, sag * 2, x0 + ox + span, -4);
      g.stroke();
    }
  }
  // Low rubble silhouettes along the bottom edge.
  for (let x = 0; x < W; x += rng.int(80, 200)) {
    const w = rng.int(40, 140);
    const h = rng.int(16, 46);
    for (const ox of [0, -W, W]) {
      g.beginPath();
      g.moveTo(x + ox, H);
      g.lineTo(x + ox + w * 0.2, H - h);
      g.lineTo(x + ox + w * 0.7, H - h * 0.7);
      g.lineTo(x + ox + w, H);
      g.closePath();
      g.fill();
    }
  }
}

/** Screen-space vertical fog gradient (tinted at runtime). */
export function fogTex(scene: Phaser.Scene): string {
  const key = 'world_fog_gradient';
  canvasTex(scene, key, 8, 256, (g) => {
    const grd = g.createLinearGradient(0, 0, 0, 256);
    grd.addColorStop(0, 'rgba(255,255,255,0)');
    grd.addColorStop(0.55, 'rgba(255,255,255,0.18)');
    grd.addColorStop(1, 'rgba(255,255,255,0.6)');
    g.fillStyle = grd;
    g.fillRect(0, 0, 8, 256);
  });
  return key;
}

// ───────────────────────────── Props ─────────────────────────────

/** Exit gate 160×208: frame 0 inactive, frame 1 active. */
export function exitGateTex(scene: Phaser.Scene): string {
  if (hasFrame(scene, TEX.exitGate, 1)) return TEX.exitGate;
  const key = 'world_fb_exit';
  if (!scene.textures.exists(key)) {
    canvasTex(scene, key, 320, 208, (g) => {
      for (let i = 0; i < 2; i++) {
        g.save();
        g.translate(i * 160, 0);
        const lit = i === 1;
        // Frame pillars and lintel.
        g.fillStyle = css(COLORS.steel1);
        g.fillRect(8, 20, 30, 188);
        g.fillRect(122, 20, 30, 188);
        g.fillRect(0, 0, 160, 32);
        g.fillStyle = css(COLORS.steel3);
        g.fillRect(8, 20, 30, 4);
        g.fillRect(122, 20, 30, 4);
        g.fillRect(0, 0, 160, 5);
        g.fillStyle = css(COLORS.ink);
        g.fillRect(36, 24, 2, 184);
        g.fillRect(122, 24, 2, 184);
        // Field.
        const c = lit ? COLORS.volt : COLORS.steel2;
        const grd = g.createLinearGradient(0, 32, 0, 208);
        grd.addColorStop(0, css(c, lit ? 0.75 : 0.3));
        grd.addColorStop(1, css(c, lit ? 0.18 : 0.08));
        g.fillStyle = grd;
        g.fillRect(38, 32, 84, 176);
        g.fillStyle = css(lit ? COLORS.voltGlow : COLORS.steel3, lit ? 0.9 : 0.5);
        for (let y = 44; y < 200; y += 18) g.fillRect(44, y, 72, 2);
        // Status lamps.
        g.fillStyle = css(lit ? COLORS.uiGood : COLORS.uiBad);
        g.fillRect(16, 10, 12, 8);
        g.fillRect(132, 10, 12, 8);
        g.restore();
      }
    });
    stripFrames(scene, key, 160, 208, 2);
  }
  return key;
}

/** Edge door 48×144: frame 0 locked, frame 1 open. */
export function doorTex(scene: Phaser.Scene): string {
  if (hasFrame(scene, TEX.door, 1)) return TEX.door;
  const key = 'world_fb_door';
  if (!scene.textures.exists(key)) {
    canvasTex(scene, key, 96, 144, (g) => {
      for (let i = 0; i < 2; i++) {
        g.save();
        g.translate(i * 48, 0);
        g.fillStyle = css(COLORS.steel1);
        g.fillRect(0, 0, 48, 144);
        g.fillStyle = css(COLORS.steel2);
        g.fillRect(6, 6, 36, 132);
        g.fillStyle = css(COLORS.ink);
        g.fillRect(23, 6, 2, 132);
        g.fillStyle = css(COLORS.telegraph, 0.5);
        for (let y = 14; y < 130; y += 24) g.fillRect(8, y, 32, 4);
        g.fillStyle = css(i === 0 ? COLORS.uiBad : COLORS.uiGood);
        g.fillRect(18, 64, 12, 12);
        g.restore();
      }
    });
    stripFrames(scene, key, 48, 144, 2);
  }
  return key;
}

export function crateTex(scene: Phaser.Scene): string {
  const painted = stageProp(scene, 'crate');
  if (painted) return painted;
  return texOr(scene, TEX.crate, () => {
    const key = 'world_fb_crate';
    canvasTex(scene, key, 64, 64, (g) => {
      g.fillStyle = css(COLORS.ink);
      g.fillRect(2, 4, 60, 60);
      g.fillStyle = css(COLORS.gunmetal);
      g.fillRect(4, 6, 56, 56);
      g.fillStyle = css(shade(COLORS.gunmetal, 1.35));
      g.fillRect(4, 6, 56, 6);
      g.strokeStyle = css(shade(COLORS.gunmetal, 0.7));
      g.lineWidth = 4;
      g.beginPath();
      g.moveTo(8, 16);
      g.lineTo(56, 58);
      g.moveTo(56, 16);
      g.lineTo(8, 58);
      g.stroke();
      g.fillStyle = css(COLORS.scrap);
      g.fillRect(26, 30, 12, 12);
      g.fillStyle = css(COLORS.telegraph, 0.7);
      g.fillRect(4, 56, 56, 3);
    });
    return key;
  });
}

export function terminalTex(scene: Phaser.Scene): string {
  const painted = stageProp(scene, 'terminal');
  if (painted) return painted;
  return texOr(scene, TEX.terminal, () => {
    const key = 'world_fb_terminal';
    canvasTex(scene, key, 64, 96, (g) => {
      g.fillStyle = css(COLORS.steel1);
      g.fillRect(20, 40, 24, 56);
      g.fillStyle = css(COLORS.steel2);
      g.fillRect(4, 8, 56, 40);
      g.fillStyle = css(COLORS.ink);
      g.fillRect(9, 13, 46, 28);
      g.fillStyle = css(COLORS.volt, 0.55);
      for (let y = 17; y < 38; y += 6) g.fillRect(13, y, 20 + ((y * 7) % 18), 2);
      g.fillStyle = css(COLORS.steel3);
      g.fillRect(4, 8, 56, 3);
      g.fillRect(14, 90, 36, 6);
    });
    return key;
  });
}

export function stationTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.station, () => {
    const key = 'world_fb_station';
    canvasTex(scene, key, 192, 192, (g) => {
      g.fillStyle = css(COLORS.steel1);
      g.fillRect(16, 40, 160, 152);
      g.fillStyle = css(COLORS.steel2);
      g.fillRect(8, 24, 176, 24);
      g.fillStyle = css(COLORS.steel3);
      g.fillRect(8, 24, 176, 4);
      g.fillStyle = css(COLORS.ink);
      g.fillRect(36, 62, 120, 70);
      g.fillStyle = css(COLORS.orbArsenal, 0.7);
      g.fillRect(44, 70, 104, 4);
      g.fillStyle = css(COLORS.uiGood, 0.5);
      for (let y = 82; y < 126; y += 9) g.fillRect(44, y, 40 + ((y * 13) % 50), 3);
      g.fillStyle = css(COLORS.steel2);
      g.fillRect(28, 146, 136, 20);
      g.fillStyle = css(COLORS.orbArsenal);
      g.fillRect(70, 30, 52, 12);
      g.fillStyle = css(COLORS.ink);
      g.fillRect(16, 184, 160, 8);
    });
    return key;
  });
}

export function repairTex(scene: Phaser.Scene): string {
  return texOr(scene, TEX.repairBay, () => {
    const key = 'world_fb_repair';
    canvasTex(scene, key, 160, 160, (g) => {
      g.fillStyle = css(COLORS.steel1);
      g.fillRect(8, 16, 24, 144);
      g.fillRect(128, 16, 24, 144);
      g.fillRect(0, 0, 160, 24);
      g.fillStyle = css(COLORS.steel3);
      g.fillRect(0, 0, 160, 4);
      g.fillStyle = css(COLORS.heal, 0.25);
      g.fillRect(32, 24, 96, 128);
      g.fillStyle = css(COLORS.heal);
      g.fillRect(72, 6, 16, 12);
      g.fillRect(74, 4, 12, 16);
      g.fillStyle = css(COLORS.steel2);
      g.fillRect(24, 148, 112, 12);
      g.fillStyle = css(COLORS.heal, 0.8);
      g.fillRect(28, 150, 104, 3);
    });
    return key;
  });
}

/** Sealed-orb containment ring (energy lock) drawn around loot-room orbs. */
export function orbShellTex(scene: Phaser.Scene, type: 'arsenal' | 'armor' | 'unstable'): string {
  return texOr(scene, TEX.orbShell[type], () => {
    const key = `world_fb_orb_${type}`;
    const color = type === 'arsenal' ? COLORS.orbArsenal : type === 'armor' ? COLORS.orbArmor : COLORS.orbUnstable;
    canvasTex(scene, key, 64, 64, (g) => {
      const grd = g.createRadialGradient(28, 26, 2, 32, 32, 30);
      grd.addColorStop(0, css(mix(color, 0xffffff, 0.6)));
      grd.addColorStop(0.6, css(color, 0.85));
      grd.addColorStop(1, css(shade(color, 0.4), 0.9));
      g.fillStyle = grd;
      g.beginPath();
      g.arc(32, 32, 28, 0, Math.PI * 2);
      g.fill();
    });
    return key;
  });
}
